import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import { Schedule, EditSchedule, NewSchedule, SavedSchedule, Status } from './types';
import { getSchedules, attachStations, detachStations, saveSchedule } from '../../api/ruleSchedules';
import { MenuNodeSlice } from '../positions/types';
import moment from 'moment';
import { setTemporaryMessage } from './settingsSlice';
import { isUndefined, notUndefined } from '../../lib/typeCheck';
//https://github.com/reduxjs/redux-toolkit/commit/b755f9d4ed6744a46ca8f05333d4731f7536196a generic slice idea for schedules

const colors = [
  { background: '#6684CC', border: '#39579F' },
  { background: '#D39A44', border: '#A57937' },
  // { background: '#CC6666', border: '#944646' },
  { background: '#8166CC', border: '#59478E' },
  { background: '#40bf44', border: '#339936' },
  
  { background: '#4066bf', border: '#263d73' },
  { background: '#ba812c', border: '#674718' },
  // { background: '#bf4040', border: '#732626' },
  { background: '#6240bf', border: '#3b2673' },
]

const getColor = (id) => {
  // return { background: '#66CC6A', border: '#49924C' };
  return colors[(id-1)%7];
}

const initialState: Array<Schedule> = [];

export const ruleSchedulesSlice = createSlice({
  name: 'ruleSchedules',
  initialState,
  reducers: {
    loadRuleSchedules: (state, action: PayloadAction<Schedule[]>) => {
      // console.log(action.payload)
      return action.payload;
    },
    editSchedule: (state, action: PayloadAction<EditSchedule>) => {
      const schedule = state.find(schedule => schedule.id === action.payload.id)
      
      if (notUndefined(action.payload.dtstart)) schedule.dtstart = action.payload.dtstart;    
      if (notUndefined(action.payload.duration)) schedule.duration = action.payload.duration;
      if (notUndefined(action.payload.title)) schedule.title = action.payload.title;
      if (notUndefined(action.payload.description)) schedule.description = action.payload.description;
      if (notUndefined(action.payload.byweekday)) schedule.byweekday = action.payload.byweekday;
      if (notUndefined(action.payload.byhour)) schedule.byhour = action.payload.byhour;
      if (notUndefined(action.payload.byminute)) schedule.byminute = action.payload.byminute;
      
      schedule.saved = false;
      if (typeof action.payload.saved != 'undefined') {
        schedule.saved = action.payload.saved;
      }
    },
    toggleScheduleRepeat: (state, action: PayloadAction<EditSchedule>) => {
      const schedule = state.find(schedule => schedule.id === action.payload.id)
      
      if (notUndefined(action.payload.dtstart)) {
        schedule.dtstart = action.payload.dtstart;
      } else if (notUndefined(action.payload.duration)) {
        schedule.duration = action.payload.duration;
      } else if (notUndefined(action.payload.title)) {
        schedule.title = action.payload.title;
      }
    },
    loadSavedSchedule: (state, action: PayloadAction<{ savedSchedule: SavedSchedule; selectedNodes?: Array<MenuNodeSlice>; }>) => {
      let next_id = 1;
      if (state.length > 0) {
        next_id = state[state.length - 1].id + 1;
      }
      
      // Unfocus other schedules
      state.forEach((schedule, index) => {
        if (state[index].focus === true) {
          state[index].focus = false;
        }
      });
      
      const { savedSchedule } = action.payload;
      
      // Move dtstart to the beginning of the week so repeating days can 
      // be added before the calendar click day.3
      // Subtract extra week for midnight spanning on sunday
      let weekstart = moment().startOf('isoWeek').subtract(1, 'week');
      let back_dated_start = weekstart.hour(parseInt(moment(savedSchedule.dtstart).format('H')));
      
      let rule = {
        id: next_id,
        station_ids: [parseInt(action.payload.selectedNodes[0].id)],
        schedules_id: savedSchedule.id,
        category: savedSchedule.category,
        status: 1,
        title: savedSchedule.title,
        description: '',
        allDay: false,
        dtstart: back_dated_start.format('YYYY-MM-DD HH:mm:ss'),
        duration: savedSchedule.duration,
        freq: 'WEEKLY',
        interval: 1,
        byweekday: savedSchedule.byweekday,
        byminute: savedSchedule.byminute,
        byhour: savedSchedule.byhour,
        until: null,
        saved: false,
        focus: true,
        editable: true,
        style: getColor(next_id)
      }
      
      if (savedSchedule.category === 'rawaudio') {
        // rule.duration = 60;
        // let start_hour = back_dated_start.hour();
        // rule.byminute = [0];
        // rule.byhour = [];
        // 
        // console.log(newSchedule.duration)
        // console.log(start_hour + (newSchedule.duration/3600))
        // for (let i = start_hour; i < start_hour + (newSchedule.duration/3600); i++) {
        //   rule.byhour.push(i);
        // }
      }
      
      state.push(rule);
    },
    newSchedule: (state, action: PayloadAction<{ newSchedule: NewSchedule; selectedNodes?: Array<MenuNodeSlice>; }>) => {
      console.log(action.payload.newSchedule)
      let next_id = 1;
      if (state.length > 0) {
        next_id = state[state.length - 1].id + 1;
      }
      
      // Unfocus other schedules
      state.forEach((schedule, index) => {
        if (state[index].focus === true) {
          state[index].focus = false;
        }
      });
      
      const { newSchedule } = action.payload;
      
      // Move dtstart to the beginning of the week so repeating days can 
      // be added before the calendar click day.3
      // Subtract extra week for midnight spanning on sunday
      let weekstart = moment().startOf('isoWeek').subtract(1, 'week');
      let back_dated_start = weekstart.hour(parseInt(moment(newSchedule.dtstart).format('H')));
      
      let rule = {
        id: next_id,
        station_ids: [parseInt(action.payload.selectedNodes[0].id)],
        schedules_id: null,
        category: newSchedule.category,
        status: 1,
        title: newSchedule.title,
        description: '',
        allDay: false,
        dtstart: back_dated_start.format('YYYY-MM-DD HH:mm:ss'),
        duration: newSchedule.duration,
        freq: 'WEEKLY',
        interval: 1,
        byweekday: newSchedule.byweekday,
        byminute: isUndefined(newSchedule.byminute)? null : newSchedule.byminute,
        byhour: isUndefined(newSchedule.byminute)? null : newSchedule.byhour,
        until: null,
        saved: false,
        focus: true,
        editable: true,
        style: getColor(next_id)
      }
      
      if (newSchedule.category === 'rawaudio') {
        // rule.duration = 60;
        // let start_hour = back_dated_start.hour();
        // rule.byminute = [0];
        // rule.byhour = [];
        // 
        // console.log(newSchedule.duration)
        // console.log(start_hour + (newSchedule.duration/3600))
        // for (let i = start_hour; i < start_hour + (newSchedule.duration/3600); i++) {
        //   rule.byhour.push(i);
        // }
      }
      
      state.push(rule);
    },
    focusSchedule: (state, action: PayloadAction<EditSchedule>) => {
      state.forEach((schedule, index) => {
        if (state[index].focus === true) {
          state[index].focus = false;
        }
        if (state[index].id === action.payload.id) {
          state[index].focus = true;
        }
      });
    },
    focusLastSchedule: (state) => {
      state.forEach((schedule, index) => {
        if (state[index].focus === true) {
          state[index].focus = false;
        }
      });
      if (state.length > 0) {
        state[state.length - 1].focus = true;
      }
    },
    removeSchedule: (state, action: PayloadAction<EditSchedule>) => {
      const schedule = state.find(schedule => schedule.id === action.payload.id)
      
      if (schedule.editable) {
        return state.filter((schedule) => schedule.id !== action.payload.id)
      }
    }
  },
});

export const { editSchedule, newSchedule, removeSchedule, focusSchedule, focusLastSchedule, loadRuleSchedules, loadSavedSchedule } = ruleSchedulesSlice.actions;

const convertToNumber = (list) => {
  return list
}

export const fetchStation = (): AppThunk => async (dispatch, getState) => {
  const { ruleSchedules, user, menuNodes } = getState()
  
  let stationIds = menuNodes.filter((node) => node.selected && node.type == 'station').map(node => parseInt(node.id))
  if (stationIds.length !== 1) {
    return;
  }
  
  try {
    const ruleSchedules = await getSchedules(user.api_key, stationIds, 'all')
    const preparedRuleSchedules = prepareRuleSchedulesForStore(ruleSchedules);
    
    // Group by schedules s they can be viewed for multiple stations
    
    // $sorted_schedules = [];
    //   foreach ($schedules as $schedule) {
    //     if (!isset($sorted_schedules[$schedule['id']])) {
    //       $sorted_schedules['id'] = $schedule;
    //       $sorted_schedules['station_ids'] = [$schedule['station_id']];
    //     } else {
    //       $sorted_schedules['station_ids'][] = $schedule['station_id'];
    //     }
    //   }
    // let schedule_hash = {};
    // 
    // filteredSchedules.forEach(schedule => {
    //   if (isUndefined(schedule_hash[schedule.schedules_id])) {
    //     schedule_hash[schedule.schedules_id] = schedule;
    //     schedule_hash[schedule.schedules_id]['station_ids'] = [schedule.station_id];
    //   } else {
    //     schedule_hash[schedule.schedules_id]['station_ids'].push(schedule.station_id);
    //   }
    // })
    // 
    // // Map back to array
    // const grouped_schedules = Object.keys(schedule_hash).map(key => ({ ...schedule_hash[key] }));
    
    dispatch(loadRuleSchedules(preparedRuleSchedules));
    dispatch(focusLastSchedule());
  } catch (err) {
    console.error(err.toString())
  }
};

export const attachStationsToSchedule = (station_ids: number[], schedules_id: number, category: string): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  try {
    const schedules = await attachStations(user.api_key, station_ids, schedules_id, category);
    // dispatch(fetchStation(station_ids[0]));
  } catch (err) {
    console.error(err.toString())
  }
};

export const removeScheduleFromDb = (schedule: EditSchedule): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  if (schedule.schedules_id === null) {
    dispatch(removeSchedule(schedule));
    dispatch(focusLastSchedule());
    return;
  }
  
  try {
    const schedules = await detachStations(user.api_key, schedule.station_ids, schedule.schedules_id, schedule.category);
    dispatch(removeSchedule({id: schedule.id}));
    // dispatch(fetchStation(schedule.station_ids[0]));
  } catch (err) {
    dispatch(setTemporaryMessage('Failed to remove'));
    console.log(err)
  }
};

export const saveScheduleToDb = (schedule: EditSchedule): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  try {
    const result = await saveSchedule(user.api_key, user.account.id, {
      schedules_id: schedule.schedules_id,
      status: schedule.status,
      title: schedule.title,
      description: schedule.description,
      freq: schedule.freq,
      dtstart: moment(schedule.dtstart).format('YYYY-MM-DD HH:mm:ss'),
      // dtstart: schedule.dtstart,
      duration: schedule.duration,
      interval: schedule.interval,
      byweekday: schedule.byweekday,
      byhour: schedule.byhour,
      byminute: schedule.byminute,
    });
    // dispatch(attachStationsToSchedule(schedule.station_ids, result.id, schedule.category))
    
    dispatch(editSchedule({id: schedule.id, status: schedule.status, saved: true}));
    dispatch(focusLastSchedule());
    // dispatch(setTemporaryMessage('Saved'));
  } catch (err) {
    dispatch(setTemporaryMessage('Failed to save'));
    
  }
};

export const saveSchedulesToDb = (schedules: EditSchedule[]): AppThunk => async (dispatch, getState) => {
  const { user } = getState()
  
  schedules.forEach(schedule => {
    if (!schedule.saved) {
      dispatch(saveScheduleToDb(schedule));
    }
  })
};

export const selectAllSchedules = (state: RootState) => {
  return state.ruleSchedules;
};

export const selectFilteredSchedules = () => (state: RootState) => {
  return state.ruleSchedules.filter(schedule => schedule.status === Status.ACTIVE);
};

export const prepareRuleSchedulesForStore = (ruleSchedules) => {
  return ruleSchedules.map((ruleSchedule, index) => {
    return {
      id: index,
      station_id: ruleSchedule.station_id,
      schedules_id: ruleSchedule.schedules_id,
      category: ruleSchedule.category,
      status: ruleSchedule.status,
      title: ruleSchedule.title,
      description: ruleSchedule.description,
      allDay: false,
      duration: ruleSchedule.duration,
      freq: ruleSchedule.freq,
      interval: ruleSchedule.interval,
      byweekday: ruleSchedule.byweekday,
      byminute: ruleSchedule.byminute,
      byhour: ruleSchedule.byhour,
      dtstart: ruleSchedule.dtstart,
      until: ruleSchedule.until,
      saved: true,
      focus: false,
      editable: true,
      style: index === 0? { background: '#66CC6A', border: '#49924C' } : getColor(index),
    }
  })
}

export default ruleSchedulesSlice.reducer;
